import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { plansAPI } from '../services/api';
import { 
  CheckCircle, 
  Zap, 
  MessageSquare, 
  Users, 
  Image, 
  Share2, 
  Globe, 
  Menu, 
  X, 
  ChevronRight, 
  Star, 
  Layout, 
  ArrowRight,
  ArrowLeft,
  UploadCloud, 
  Rocket,      
  CheckSquare, 
  FileText,
  Shield,
  Headphones,
  BarChart3,
  Layers,
  Sparkles
} from 'lucide-react';

// --- Global Styles & Font Injection ---
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@200;300;400;500;600;700;800&display=swap');
    
    body {
      font-family: 'Cairo', sans-serif;
    }
    
    .font-cairo {
      font-family: 'Cairo', sans-serif;
    }

    @keyframes float {
      0% { transform: translateY(0px); }
      50% { transform: translateY(-20px); }
      100% { transform: translateY(0px); }
    }
    
    .float-animation {
      animation: float 6s ease-in-out infinite;
    }

    .grid-pattern {
      background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230ea5e9' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    }
    
    .dots-pattern {
      background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='3'/%3E%3Ccircle cx='13' cy='13' r='3'/%3E%3C/g%3E%3C/svg%3E");
    }
  `}</style>
);

// --- Translations ---
const translations = {
  ar: {
    nav: {
      features: "الميزات",
      howItWorks: "كيف يعمل",
      pricing: "الأسعار",
      login: "تسجيل الدخول",
      signup: "ابدأ مجاناً",
      startNow: "ابدأ الآن",
    },
    hero: {
      badge: "المنصة الأولى لإدارة المحتوى",
      title1: "نظام سَلِس لإدارة",
      title2: "واعتماد المحتوى",
      subtitle: "منصة شاملة لإدارة محتوى وكالات التسويق مع عملائها. اعتماد سريع، تعاون فعال، وإدارة احترافية لكل منشوراتك في مكان واحد.",
      primaryBtn: "ابدأ الآن",
      secondaryBtn: "إنشاء حساب",
    },
    features: {
      badge: "لماذا سَلِس؟",
      title: "أدوات قوية مصممة للنمو",
      subtitle: "نجمع بين سهولة الاستخدام والميزات المتقدمة لتمكين فريقك",
      list: [
        { title: "إدارة مركزية", desc: "إدارة جميع منشوراتك من مكان واحد. إنستقرام، تويتر، فيسبوك والمزيد.", icon: Layout },
        { title: "اعتماد سريع", desc: "نظام اعتماد مبسط. العملاء يعتمدون أو يطلبون تعديلات بنقرة واحدة.", icon: CheckCircle },
        { title: "تعليقات تفاعلية", desc: "أضف تعليقات مباشرة على الصور والتصاميم بدقة متناهية.", icon: MessageSquare },
        { title: "إدارة الفريق", desc: "نظم فريقك بسهولة. حدد المسؤوليات وتتبع التقدم لحظة بلحظة.", icon: Users },
        { title: "مكتبة الوسائط", desc: "مكتبة شاملة لجميع صورك وفيديوهاتك مع خاصية الرفع الجماعي.", icon: Image },
        { title: "نشر مباشر", desc: "شارك منشوراتك المعتمدة مباشرة على منصات التواصل بضغطة زر.", icon: Share2 },
      ]
    },
    howItWorks: {
      title: "كيف يعمل سَلِس؟",
      subtitle: "نحول الفوضى إلى نظام متناغم في 3 خطوات عملية",
      steps: [
        { 
          stepLabel: "الخطوة الأولى",
          heading: "التجهيز والرفع المركزي",
          desc: "ابدأ بإنشاء مشروع جديد. ارفع التصاميم واكتب المحتوى في محرر متطور. نظم كل شيء في مجلدات.",
          features: ["رفع بالسحب والإفلات", "محرر نصوص ذكي"],
          icon: UploadCloud 
        },
        { 
          stepLabel: "الخطوة الثانية",
          heading: "المراجعة والاعتماد",
          desc: "شارك رابطاً مع العميل. يمكنه النقر على التصميم لإضافة ملاحظة دقيقة أو الاعتماد بضغطة زر.",
          features: ["معاينة حية للمنشور", "تعليقات دقيقة"],
          icon: CheckSquare
        },
        { 
          stepLabel: "الخطوة الثالثة",
          heading: "النشر والتحليل",
          desc: "بمجرد الاعتماد، ينتقل المنشور للنشر تلقائياً أو جدولته، مع أرشيف كامل.",
          features: ["نشر مباشر", "جدولة ذكية"],
          icon: Rocket
        },
      ]
    },
    stats: {
      title: "أرقام تتحدث عن نجاحنا",
      list: [
        { value: "500+", label: "وكالة تسويق" },
        { value: "50K+", label: "منشور معتمد" },
        { value: "80%", label: "توفير وقت" },
        { value: "99%", label: "رضا العملاء" },
      ]
    },
    testimonials: {
      title: "شركاء النجاح",
      badge: "ماذا يقول عملاؤنا",
      list: [
        { name: "أحمد محمد", role: "مدير وكالة إبداع", text: "سَلِس غيّر طريقة عملنا بالكامل. الآن الاعتماد أسرع وأسهل بكثير!", avatar: "https://i.pravatar.cc/150?u=1" },
        { name: "سارة العلي", role: "مسؤولة تسويق", text: "أفضل منصة استخدمناها لإدارة المحتوى. أنصح بها بشدة!", avatar: "https://i.pravatar.cc/150?u=2" },
      ]
    },
    pricing: {
      title: "خطط تناسب الجميع",
      subtitle: "اختر الخطة المثالية لفريقك",
      period: "/شهرياً",
      btn: "اختر الخطة",
      popular: "الأكثر شعبية"
    },
    cta: {
      title: "جاهز لتبدأ رحلتك؟",
      subtitle: "انضم إلى مئات الوكالات التي تستخدم سَلِس اليوم",
      btn: "ابدأ تجربتك المجانية"
    },
    footer: {
      description: "سَلِس هو الحل الأمثل لإدارة واعتماد المحتوى الرقمي للوكالات والفرق الإبداعية.",
      links: ["الرئيسية", "الحلول", "المميزات", "تواصل معنا"],
      rights: "© 2024 سَلِس. جميع الحقوق محفوظة."
    }
  },
  en: {
    nav: {
      features: "Features",
      howItWorks: "How it Works",
      pricing: "Pricing",
      login: "Login",
      signup: "Start Free",
      startNow: "Start Now",
    },
    hero: {
      badge: "The #1 Content Platform",
      title1: "sls Content Management",
      title2: "& Approval System",
      subtitle: "A comprehensive platform for marketing agencies. Fast approval, effective collaboration, and professional management in one place.",
      primaryBtn: "Start Now",
      secondaryBtn: "Create Account",
    },
    features: {
      badge: "Why sls?",
      title: "Powerful Tools for Growth",
      subtitle: "Combining ease of use with advanced features to empower your team",
      list: [
        { title: "Centralized Management", desc: "Manage all posts from one place. Instagram, Twitter, Facebook, and more.", icon: Layout },
        { title: "Fast Approval", desc: "Streamlined approval. Clients approve or request changes with one click.", icon: CheckCircle },
        { title: "Interactive Comments", desc: "Add comments directly on images and designs with pinpoint accuracy.", icon: MessageSquare },
        { title: "Team Management", desc: "Organize your team easily. Assign responsibilities and track progress.", icon: Users },
        { title: "Media Library", desc: "Comprehensive library for all images and videos with bulk upload.", icon: Image },
        { title: "Direct Publishing", desc: "Share approved posts directly to social platforms with one click.", icon: Share2 },
      ]
    },
    howItWorks: {
      title: "How sls Works",
      subtitle: "Turn chaos into a harmonious system in 3 steps",
      steps: [
        { 
          stepLabel: "Step 01",
          heading: "Setup & Upload",
          desc: "Create a project. Upload designs and write content in an advanced editor. Organize everything in folders.",
          features: ["Drag & drop", "Smart editor"],
          icon: UploadCloud 
        },
        { 
          stepLabel: "Step 02",
          heading: "Review & Approve",
          desc: "Share a link with the client. They can click on designs to leave feedback or approve instantly.",
          features: ["Live preview", "Pinpoint comments"],
          icon: CheckSquare
        },
        { 
          stepLabel: "Step 03",
          heading: "Publish & Analyze",
          desc: "Once approved, the post moves to publishing automatically or is scheduled.",
          features: ["Direct publishing", "Smart scheduling"],
          icon: Rocket
        },
      ]
    },
    stats: {
      title: "Our Success Numbers",
      list: [
        { value: "500+", label: "Agency" },
        { value: "50K+", label: "Approved Post" },
        { value: "80%", label: "Time Saved" },
        { value: "99%", label: "Client Satisfaction" },
      ]
    },
    testimonials: {
      title: "Success Partners",
      badge: "What Clients Say",
      list: [
        { name: "Ahmed Mohamed", role: "Agency CEO", text: "sls completely changed how we work. Approval is now much faster!", avatar: "https://i.pravatar.cc/150?u=1" },
        { name: "Sarah Al-Ali", role: "Marketing Manager", text: "Best platform we've used for content management. Highly recommend!", avatar: "https://i.pravatar.cc/150?u=2" },
      ]
    },
    pricing: {
      title: "Plans for Everyone",
      subtitle: "Choose the perfect plan for your team",
      period: "/month",
      btn: "Choose Plan",
      popular: "Most Popular"
    },
    cta: {
      title: "Ready to Start?",
      subtitle: "Join hundreds of agencies using sls today",
      btn: "Start Free Trial"
    },
    footer: {
      description: "sls is the ultimate solution for digital content management and approval.",
      links: ["Home", "Solutions", "Features", "Contact"],
      rights: "© 2024 sls. All rights reserved."
    }
  }
};

// Fallback plans when API fails
const FALLBACK_PLANS = [
  { id: '1', name: "Starter", name_ar: "المبتدئ", price: "$0", features: ["1 Project", "2 Users", "Basic Analytics"], popular: false },
  { id: '2', name: "Pro", name_ar: "المحترف", price: "$29", features: ["10 Projects", "10 Users", "Advanced Analytics", "Direct Publishing"], popular: true },
  { id: '3', name: "Agency", name_ar: "الوكالة", price: "$99", features: ["Unlimited Projects", "Unlimited Users", "White Labeling", "Priority Support"], popular: false },
];

// --- Components ---

const Button = ({ children, variant = 'primary', className = '', onClick, ...props }) => {
  const baseStyles = "inline-flex items-center justify-center px-6 py-3 rounded-xl font-bold transition-all duration-300 focus:outline-none transform hover:-translate-y-0.5";
  
  const variants = {
    primary: "bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-600/30",
    secondary: "bg-white text-slate-700 hover:text-sky-600 border border-slate-200 hover:border-sky-600 shadow-sm",
    accent: "bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/30",
    white: "bg-white text-sky-700 hover:bg-sky-50 shadow-md",
  };

  return (
    <button className={`${baseStyles} ${variants[variant]} ${className}`} onClick={onClick} {...props}>
      {children}
    </button>
  );
};

const InteractiveDemo = ({ isRTL }) => {
  const [status, setStatus] = useState('pending');
  const [showCommentTooltip, setShowCommentTooltip] = useState(false);
  const [comments, setComments] = useState([
    { id: 1, x: 30, y: 25, user: 'أحمد محمد', text: 'يمكن تغيير اللون إلى الأزرق؟', avatar: 'https://i.pravatar.cc/150?u=1' },
    { id: 2, x: 65, y: 60, user: 'Sarah', text: 'Love this design!', avatar: 'https://i.pravatar.cc/150?u=2' },
  ]);

  const handleAction = (newStatus) => {
    setStatus(newStatus);
    setTimeout(() => setStatus('pending'), 2500);
  };

  // Demo post image - Nike shoe product
  const DEMO_IMAGE = "https://images.unsplash.com/photo-1542291026-7eec264c27ff?q=80&w=1000&auto=format&fit=crop";

  return (
    <div className="relative mx-auto max-w-5xl">
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl p-4 md:p-6 border border-slate-700 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgb(255 255 255 / 0.15) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Browser Chrome */}
        <div className="relative z-10 bg-slate-800 rounded-t-xl p-3 flex items-center gap-2 border-b border-slate-700">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <div className="flex-1 bg-slate-900 rounded px-3 py-1.5 text-slate-400 text-xs flex items-center gap-2">
            <Globe size={12} />
            <span>{isRTL ? 'sls.sa/post/demo' : 'sls.sa/post/demo'}</span>
          </div>
        </div>

        {/* Content Area */}
        <div className="relative z-10 bg-white rounded-b-xl overflow-hidden">
          {/* Success/Changes Overlay */}
          {status === 'approved' && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-emerald-500/95 backdrop-blur-sm animate-in fade-in zoom-in duration-300">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-emerald-500 mb-4 shadow-2xl animate-bounce">
                <CheckCircle size={40} strokeWidth={3} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">{isRTL ? 'تم الاعتماد!' : 'Approved!'}</h3>
              <p className="text-white/90 text-sm">{isRTL ? 'سيتم النشر قريباً' : 'Ready to publish'}</p>
            </div>
          )}
          
          {status === 'changes' && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-amber-500/95 backdrop-blur-sm animate-in fade-in zoom-in duration-300">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-amber-500 mb-4 shadow-2xl animate-bounce">
                <MessageSquare size={40} strokeWidth={3} />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">{isRTL ? 'طلب تعديلات' : 'Changes Requested'}</h3>
              <p className="text-white/90 text-sm">{isRTL ? 'تم إرسال التعليقات' : 'Feedback sent to team'}</p>
            </div>
          )}

          {/* Post Header */}
          <div className="p-4 border-b border-slate-100 bg-slate-50">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">
                  {isRTL ? 'منشور Instagram - منتج جديد' : 'Instagram Post - New Product'}
                </h3>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Users size={14} />
                  <span>{isRTL ? 'عميل: Nike Arabia' : 'Client: Nike Arabia'}</span>
                </div>
              </div>
              <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
                {isRTL ? 'قيد المراجعة' : 'Pending'}
              </span>
            </div>
          </div>

          {/* Main Content */}
          <div className={`p-4 transition-all duration-500 ${status !== 'pending' ? 'opacity-0' : 'opacity-100'}`}>
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Image with Comments */}
              <div className="flex-1">
                <div className="relative aspect-square bg-slate-100 rounded-xl overflow-hidden group">
                  <img 
                    src={DEMO_IMAGE} 
                    alt="Product" 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  
                  {/* Comment Pins */}
                  {comments.map((comment) => (
                    <div
                      key={comment.id}
                      className="absolute cursor-pointer"
                      style={{ left: `${comment.x}%`, top: `${comment.y}%` }}
                      onMouseEnter={() => setShowCommentTooltip(comment.id)}
                      onMouseLeave={() => setShowCommentTooltip(false)}
                    >
                      <div className="relative -ml-4 -mt-4">
                        <button className="w-8 h-8 bg-sky-500 hover:bg-sky-600 border-2 border-white rounded-full shadow-lg flex items-center justify-center text-white text-xs font-bold transition-all hover:scale-110 animate-pulse">
                          💬
                        </button>
                        
                        {/* Tooltip */}
                        {showCommentTooltip === comment.id && (
                          <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48">
                            <div className="bg-white rounded-lg shadow-2xl border border-slate-200 p-3 animate-in fade-in zoom-in duration-200">
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
                                <div className="border-4 border-transparent border-t-white"></div>
                              </div>
                              <div className="flex items-center gap-2 mb-2">
                                <img src={comment.avatar} alt={comment.user} className="w-6 h-6 rounded-full" />
                                <span className="text-xs font-bold text-slate-900">{comment.user}</span>
                              </div>
                              <p className="text-xs text-slate-600">{comment.text}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Add Comment Indicator */}
                  <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-full shadow-lg text-xs font-bold text-slate-700 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <MessageSquare size={14} />
                    {isRTL ? 'انقر لإضافة تعليق' : 'Click to comment'}
                  </div>
                </div>
              </div>

              {/* Post Details */}
              <div className="lg:w-80">
                <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">{isRTL ? 'النص' : 'Caption'}</label>
                    <p className="text-sm text-slate-700 mt-1 leading-relaxed">
                      {isRTL 
                        ? '🔥 الأحذية الجديدة وصلت! تصميم عصري ومريح للغاية. #Nike #Style'
                        : '🔥 New kicks just dropped! Modern design meets ultimate comfort. #Nike #Style'}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-3 text-xs text-slate-500 pt-2 border-t border-slate-200">
                    <div className="flex items-center gap-1">
                      <Layout size={12} />
                      <span>Instagram</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Image size={12} />
                      <span>1:1 {isRTL ? 'مربع' : 'Square'}</span>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-200">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                      <Users size={12} />
                      <span>{isRTL ? '2 تعليقات جديدة' : '2 comments'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-4 pt-4 border-t border-slate-100">
              <button 
                onClick={() => handleAction('changes')} 
                className="flex-1 py-3 px-4 rounded-xl border-2 border-slate-200 text-slate-700 hover:border-amber-500 hover:text-amber-600 hover:bg-amber-50 transition-all text-sm font-bold flex items-center justify-center gap-2 group"
              >
                <X size={18} className="group-hover:rotate-90 transition-transform" />
                {isRTL ? 'طلب تعديلات' : 'Request Changes'}
              </button>
              <button 
                onClick={() => handleAction('approved')} 
                className="flex-1 py-3 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white hover:shadow-xl transition-all text-sm font-bold flex items-center justify-center gap-2 group"
              >
                <CheckCircle size={18} className="group-hover:scale-110 transition-transform" />
                {isRTL ? 'اعتماد التصميم' : 'Approve Design'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Decorative elements */}
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-sky-400/20 rounded-full blur-3xl -z-10 animate-pulse"></div>
      <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-emerald-400/20 rounded-full blur-3xl -z-10 animate-pulse delay-700"></div>
    </div>
  );
};

const useLanguage = () => {
  const [lang, setLang] = useState('ar');
  const toggleLanguage = () => setLang(prev => prev === 'ar' ? 'en' : 'ar');
  return { lang, toggleLanguage, t: translations[lang], isRTL: lang === 'ar' };
};

// --- Main App ---

const LandingPage = () => {
  const { lang, toggleLanguage, t, isRTL } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch plans from API for pricing section
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await plansAPI.getAll();
        const apiPlans = response.data?.plans || [];
        if (apiPlans.length > 0) {
          const displayPlans = apiPlans.map((p, idx) => {
            const monthlyPrice = p.final_monthly_price ?? p.monthly_price ?? p.price ?? 0;
            const priceStr = monthlyPrice === 0 ? '$0' : `$${parseFloat(monthlyPrice).toFixed(0)}`;
            const featureNames = (p.features || []).map(f => (isRTL && f.name_ar) ? f.name_ar : (f.name || f.description || ''));
            const legacyFeatures = p.features_legacy;
            let featuresList = featureNames.length > 0 ? featureNames : [];
            if (featuresList.length === 0 && legacyFeatures) {
              featuresList = Array.isArray(legacyFeatures) ? legacyFeatures : Object.values(legacyFeatures || {});
            }
            if (featuresList.length === 0 && (p.max_posts != null || p.max_users != null)) {
              if (p.max_posts != null) featuresList.push(`${p.max_posts} ${isRTL ? 'منشور' : 'Posts'}`);
              if (p.max_users != null) featuresList.push(`${p.max_users} ${isRTL ? 'مستخدم' : 'Users'}`);
            }
            return {
              id: p.id,
              name: p.name,
              name_ar: p.name_ar,
              price: priceStr,
              features: featuresList.length > 0 ? featuresList : [p.description || ''],
              popular: idx === 1 || (p.display_order === 2),
            };
          });
          setPlans(displayPlans);
        }
      } catch (err) {
        console.error('Failed to fetch plans for landing:', err);
      }
    };
    fetchPlans();
  }, [isRTL]);

  const handleSignup = () => {
    navigate('/signup');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  // Primary CTA: "ابدأ الآن" / "Start Now" → Login page
  const handleStartNow = () => {
    navigate('/login');
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className={`min-h-screen bg-slate-50 font-cairo text-slate-800 ${isRTL ? 'text-right' : 'text-left'}`}>
      <GlobalStyles />
      
      {/* --- Navbar --- */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'}`}>
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2 group cursor-pointer">
            <div className="w-10 h-10 bg-gradient-to-br from-sky-600 to-emerald-500 rounded-xl flex items-center justify-center text-white shadow-lg group-hover:shadow-sky-500/30 transition-all">
              <Layers size={22} />
            </div>
            <span className="text-2xl font-bold text-slate-900 tracking-wide">
              {isRTL ? 'سَلِس' : 'sls'}<span className="text-sky-600">.</span>
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8 text-base font-medium">
            <a href="#features" className="text-slate-600 hover:text-sky-600 transition-colors">{t.nav.features}</a>
            <a href="#how-it-works" className="text-slate-600 hover:text-sky-600 transition-colors">{t.nav.howItWorks}</a>
            <a href="#pricing" className="text-slate-600 hover:text-sky-600 transition-colors">{t.nav.pricing}</a>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <button onClick={toggleLanguage} className="px-4 py-2 text-slate-600 hover:text-sky-600 border border-slate-200 hover:border-sky-600 rounded-lg transition-all font-bold text-sm">
              {lang === 'ar' ? 'EN' : 'عربي'}
            </button>
            <Button variant="secondary" className="py-2 px-6 text-sm" onClick={handleLogin}>
              {t.nav.login}
            </Button>
            <Button variant="primary" className="py-2 px-6 text-sm" onClick={handleStartNow}>
              {t.nav.startNow}
            </Button>
          </div>

          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-slate-700">
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="absolute top-full left-0 w-full bg-white shadow-lg border-t border-slate-100 p-4 flex flex-col gap-4 md:hidden">
            <a href="#features" className="block py-2 text-slate-600 font-bold hover:text-sky-600 bg-slate-50 rounded px-4" onClick={() => setMobileMenuOpen(false)}>{t.nav.features}</a>
            <a href="#how-it-works" className="block py-2 text-slate-600 font-bold hover:text-sky-600 bg-slate-50 rounded px-4" onClick={() => setMobileMenuOpen(false)}>{t.nav.howItWorks}</a>
            <a href="#pricing" className="block py-2 text-slate-600 font-bold hover:text-sky-600 bg-slate-50 rounded px-4" onClick={() => setMobileMenuOpen(false)}>{t.nav.pricing}</a>
            <div className="flex gap-3 mt-2">
              <button onClick={toggleLanguage} className="flex-1 py-3 text-center border border-slate-200 rounded-lg font-bold text-slate-600">
                {lang === 'ar' ? 'English' : 'عربي'}
              </button>
              <Button variant="secondary" className="flex-1 justify-center" onClick={handleLogin}>{t.nav.login}</Button>
              <Button variant="primary" className="flex-1 justify-center" onClick={handleStartNow}>{t.nav.startNow}</Button>
            </div>
          </div>
        )}
      </nav>

      {/* --- Hero Section --- */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
        {/* Background Patterns */}
        <div className="absolute top-0 right-0 -z-10 w-full h-full bg-slate-50">
           <div className="absolute inset-0 grid-pattern opacity-60"></div>
           <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-bl from-sky-100/40 to-transparent rounded-bl-full opacity-60 blur-3xl"></div>
           <div className="absolute bottom-0 left-0 w-1/3 h-2/3 bg-gradient-to-tr from-emerald-100/30 to-transparent rounded-tr-full opacity-60 blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2 text-center lg:text-start">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-50 border border-sky-100 text-sky-600 text-sm font-bold mb-6">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-sky-500"></span>
                </span>
                {t.hero.badge}
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6">
                {t.hero.title1} <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-emerald-500">
                  {t.hero.title2}
                </span>
              </h1>
              
              <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {t.hero.subtitle}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button variant="primary" className="gap-2 text-lg px-8 py-4" onClick={handleStartNow}>
                   {t.hero.primaryBtn}
                   {isRTL ? <ArrowLeft size={20} /> : <ArrowRight size={20} />}
                </Button>
                <Button variant="secondary" className="text-lg px-8 py-4" onClick={handleSignup}>
                   {t.hero.secondaryBtn}
                </Button>
              </div>
            </div>

            <div className="lg:w-1/2 w-full">
              <InteractiveDemo isRTL={isRTL} />
            </div>
          </div>
        </div>
      </section>

      {/* --- Trusted Section --- */}
      <section className="py-10 border-y border-slate-200 bg-white">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm font-bold mb-6 uppercase tracking-wider">{t.testimonials.title}</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
             <div className="text-2xl font-bold text-slate-400 flex items-center gap-2"><Sparkles /> AgencyOne</div>
             <div className="text-2xl font-bold text-slate-400 flex items-center gap-2"><Layout /> CreativeLab</div>
             <div className="text-2xl font-bold text-slate-400 flex items-center gap-2"><Zap /> SparkMedia</div>
             <div className="text-2xl font-bold text-slate-400 flex items-center gap-2"><Globe /> GlobalAds</div>
          </div>
        </div>
      </section>

      {/* --- Features (Cards Style) --- */}
      <section id="features" className="py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-sky-600 font-bold tracking-wider uppercase text-sm mb-2 block">{t.features.badge}</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">{t.features.title}</h2>
            <p className="text-slate-600 text-lg">{t.features.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.features.list.map((feature, idx) => (
              <div key={idx} className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 border border-slate-100">
                <div className="w-14 h-14 bg-sky-50 rounded-xl flex items-center justify-center text-sky-600 mb-6 group-hover:scale-110 group-hover:bg-sky-600 group-hover:text-white transition-all duration-300">
                  <feature.icon size={28} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm mb-6">{feature.desc}</p>
                <div className="flex items-center text-sky-600 text-sm font-bold group-hover:gap-2 transition-all cursor-pointer">
                  {isRTL ? 'اقرأ المزيد' : 'Read More'} 
                  {isRTL ? <ArrowLeft size={16} className="mr-2" /> : <ArrowRight size={16} className="ml-2" />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- How It Works (Clean Style) --- */}
      <section id="how-it-works" className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            
            <div className="lg:w-1/2">
              <span className="text-sky-600 font-bold tracking-wider uppercase text-sm mb-2 block">{t.nav.howItWorks}</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-6">{t.howItWorks.title}</h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-10">{t.howItWorks.subtitle}</p>
              
              <div className="space-y-8">
                {t.howItWorks.steps.map((step, idx) => {
                  const Icon = step.icon;
                  return (
                    <div key={idx} className="flex gap-6">
                      <div className="flex-shrink-0">
                         <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold text-xl border-2 border-emerald-100">
                           {idx + 1}
                         </div>
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2">
                           {step.heading}
                        </h4>
                        <p className="text-slate-500 text-sm leading-relaxed mb-3">{step.desc}</p>
                        <div className="flex flex-wrap gap-2">
                          {step.features.map((f, i) => (
                            <span key={i} className="text-xs font-bold bg-slate-100 text-slate-600 px-2 py-1 rounded">
                              {f}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="lg:w-1/2 relative">
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-sky-50 rounded-full blur-3xl -z-10"></div>
               
               {/* Workflow Animation */}
               <div className="relative">
                 <div className="bg-white rounded-2xl shadow-2xl p-6 space-y-4">
                   {/* Step 1: Upload */}
                   <div className="flex items-start gap-4 p-4 bg-emerald-50 border-2 border-emerald-200 rounded-xl transform hover:scale-105 transition-all duration-300">
                     <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow-lg">
                       <UploadCloud size={24} />
                     </div>
                     <div className="flex-1">
                       <h4 className="font-bold text-slate-900 mb-1">{t.howItWorks.steps[0].heading}</h4>
                       <p className="text-sm text-slate-600">{isRTL ? 'تم رفع 3 صور جديدة' : '3 images uploaded'}</p>
                       <div className="mt-2 flex gap-2">
                         <div className="w-12 h-12 bg-slate-200 rounded animate-pulse"></div>
                         <div className="w-12 h-12 bg-slate-200 rounded animate-pulse delay-100"></div>
                         <div className="w-12 h-12 bg-slate-200 rounded animate-pulse delay-200"></div>
                       </div>
                     </div>
                     <CheckCircle className="text-emerald-500 flex-shrink-0" size={20} />
                   </div>

                   {/* Arrow Down */}
                   <div className="flex justify-center">
                     <div className="w-0.5 h-8 bg-gradient-to-b from-emerald-200 to-amber-200"></div>
                   </div>

                   {/* Step 2: Review (Animated) */}
                   <div className="relative p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-300 rounded-xl shadow-lg">
                     <div className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 rounded-full animate-ping"></div>
                     <div className="flex items-start gap-4">
                       <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow-lg animate-pulse">
                         <MessageSquare size={24} />
                       </div>
                       <div className="flex-1">
                         <h4 className="font-bold text-slate-900 mb-1">{t.howItWorks.steps[1].heading}</h4>
                         <p className="text-sm text-slate-600">{isRTL ? 'جاري المراجعة...' : 'Under review...'}</p>
                         <div className="mt-3 space-y-2">
                           <div className="flex items-center gap-2 text-xs">
                             <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                             <span className="text-slate-600">{isRTL ? 'تعليق جديد من العميل' : 'New comment from client'}</span>
                           </div>
                           <div className="bg-white rounded-lg p-2 shadow-sm">
                             <div className="flex items-center gap-2 mb-1">
                               <img src="https://i.pravatar.cc/150?u=client" alt="Client" className="w-5 h-5 rounded-full" />
                               <span className="text-xs font-bold text-slate-700">{isRTL ? 'أحمد (العميل)' : 'Ahmed (Client)'}</span>
                             </div>
                             <p className="text-xs text-slate-600">{isRTL ? 'يمكن تغيير الخلفية؟' : 'Can we change the background?'}</p>
                           </div>
                         </div>
                       </div>
                       <div className="flex flex-col gap-1">
                         <button className="px-3 py-1 bg-white border border-amber-300 rounded text-xs font-bold text-amber-700 hover:bg-amber-50 transition-all">
                           {isRTL ? 'رد' : 'Reply'}
                         </button>
                       </div>
                     </div>
                   </div>

                   {/* Arrow Down */}
                   <div className="flex justify-center">
                     <div className="w-0.5 h-8 bg-gradient-to-b from-amber-200 to-sky-200"></div>
                   </div>

                   {/* Step 3: Publish */}
                   <div className="flex items-start gap-4 p-4 bg-sky-50 border-2 border-sky-200 rounded-xl transform hover:scale-105 transition-all duration-300">
                     <div className="w-12 h-12 bg-sky-500 rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow-lg">
                       <Rocket size={24} />
                     </div>
                     <div className="flex-1">
                       <h4 className="font-bold text-slate-900 mb-1">{t.howItWorks.steps[2].heading}</h4>
                       <p className="text-sm text-slate-600">{isRTL ? 'جاهز للنشر' : 'Ready to publish'}</p>
                       <div className="mt-2 flex items-center gap-2">
                         <Share2 size={14} className="text-sky-500" />
                         <span className="text-xs text-slate-500">Instagram, Facebook</span>
                       </div>
                     </div>
                     <button className="px-4 py-2 bg-sky-500 text-white rounded-lg text-xs font-bold hover:bg-sky-600 transition-all shadow-lg hover:shadow-xl">
                       {isRTL ? 'نشر' : 'Publish'}
                     </button>
                   </div>
                 </div>

                 {/* Floating Elements */}
                 <div className="absolute -top-4 -right-4 w-20 h-20 bg-sky-400/20 rounded-full blur-2xl animate-pulse"></div>
                 <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-emerald-400/20 rounded-full blur-2xl animate-pulse delay-700"></div>
               </div>
            </div>

          </div>
        </div>
      </section>

      {/* --- Stats Section (Dark Strip) --- */}
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 dots-pattern opacity-20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {t.stats.list.map((stat, idx) => (
              <div key={idx} className="p-4">
                 <div className="text-4xl md:text-5xl font-extrabold text-sky-400 mb-2">{stat.value}</div>
                 <div className="text-slate-300 font-medium text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- Pricing Section --- */}
      <section id="pricing" className="py-24 bg-slate-50">
         <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-sky-600 font-bold tracking-wider uppercase text-sm mb-2 block">{t.pricing.title}</span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">{t.pricing.subtitle}</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto items-center">
              {plans.map((plan) => (
                <div key={plan.id} className={`relative bg-white rounded-2xl p-8 border transition-all duration-300 ${plan.popular ? 'border-sky-500 shadow-2xl scale-105 z-10' : 'border-slate-100 shadow-sm hover:shadow-lg'}`}>
                   {plan.popular && (
                     <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-sky-600 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
                        {t.pricing.popular}
                     </div>
                   )}
                   <h3 className="text-xl font-bold text-slate-900 mb-2">{isRTL && plan.name_ar ? plan.name_ar : plan.name}</h3>
                   <div className="flex items-baseline mb-6">
                      <span className="text-4xl font-extrabold text-slate-900">{plan.price}</span>
                      <span className="text-slate-500 text-sm font-medium ml-1">{t.pricing.period}</span>
                   </div>
                   <ul className="space-y-4 mb-8">
                      {(plan.features || []).map((feat, i) => (
                        <li key={i} className="flex items-center gap-3 text-slate-600 text-sm font-medium">
                           <CheckCircle size={18} className="text-emerald-500 flex-shrink-0" />
                           {typeof feat === 'string' ? feat : (feat?.name_ar && isRTL ? feat.name_ar : feat?.name || feat?.description || '')}
                        </li>
                      ))}
                   </ul>
                   <Button variant={plan.popular ? 'primary' : 'secondary'} className="w-full" onClick={handleStartNow}>
                      {t.pricing.btn}
                   </Button>
                </div>
              ))}
            </div>
         </div>
      </section>

      {/* --- Contact / CTA Section --- */}
      <section id="contact" className="py-24 bg-white relative">
         <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto bg-gradient-to-br from-sky-600 to-slate-900 rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row relative">
               <div className="absolute inset-0 dots-pattern opacity-10"></div>
               
               {/* Decorative Circles */}
               <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/10 rounded-full blur-2xl"></div>
               <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-emerald-500/20 rounded-full blur-2xl"></div>

               <div className="md:w-1/2 p-12 text-white flex flex-col justify-center relative z-10">
                  <h3 className="text-3xl font-bold mb-6">{t.cta.title}</h3>
                  <p className="text-sky-100 text-lg mb-8 leading-relaxed">
                     {t.cta.subtitle}
                  </p>
                  <div className="flex flex-col gap-4">
                     <div className="flex items-center gap-4 bg-white/10 p-4 rounded-xl backdrop-blur-sm">
                        <CheckCircle className="text-emerald-400" />
                        <span className="font-bold">{isRTL ? 'تجربة مجانية لمدة 14 يوم' : '14-day free trial'}</span>
                     </div>
                     <div className="flex items-center gap-4 bg-white/10 p-4 rounded-xl backdrop-blur-sm">
                        <Zap className="text-emerald-400" />
                        <span className="font-bold">{isRTL ? 'لا يحتاج لبطاقة ائتمانية' : 'No credit card required'}</span>
                     </div>
                  </div>
               </div>

               <div className="md:w-1/2 p-12 bg-white/5 backdrop-blur-sm flex items-center justify-center relative z-10">
                  <Button variant="white" className="w-full py-4 text-lg shadow-xl" onClick={handleStartNow}>
                     {t.cta.btn}
                  </Button>
               </div>
            </div>
         </div>
      </section>

      {/* --- Footer --- */}
      <footer className="bg-slate-900 text-white pt-16 pb-8 border-t border-slate-800">
         <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
               <div className="col-span-1 md:col-span-1">
                  <div className="flex items-center gap-2 mb-6">
                     <div className="w-8 h-8 bg-gradient-to-br from-sky-600 to-emerald-500 rounded-lg flex items-center justify-center text-white">
                        <Layers size={18} />
                     </div>
                     <span className="text-xl font-bold">{isRTL ? 'سَلِس' : 'sls'}<span className="text-sky-500">.</span></span>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed">
                     {t.footer.description}
                  </p>
               </div>

               <div>
                  <h4 className="font-bold text-lg mb-6">{isRTL ? 'روابط سريعة' : 'Quick Links'}</h4>
                  <ul className="space-y-3 text-slate-400 text-sm">
                     {t.footer.links.map((link, i) => (
                        <li key={i}><a href="#" className="hover:text-sky-400 transition-colors">{link}</a></li>
                     ))}
                  </ul>
               </div>

               <div>
                  <h4 className="font-bold text-lg mb-6">{isRTL ? 'تواصل معنا' : 'Contact Us'}</h4>
                  <ul className="space-y-3 text-slate-400 text-sm">
                     <li className="flex items-center gap-2"><Globe size={16}/> www.sls.sa</li>
                     <li className="flex items-center gap-2"><Share2 size={16}/> @sls_app</li>
                  </ul>
               </div>

               <div>
                  <h4 className="font-bold text-lg mb-6">{isRTL ? 'النشرة البريدية' : 'Newsletter'}</h4>
                  <div className="flex gap-2">
                     <input type="email" placeholder={isRTL ? 'البريد الإلكتروني' : 'Email'} className="bg-slate-800 border border-slate-700 text-white px-4 py-2 rounded-lg w-full focus:outline-none focus:border-sky-500 text-sm" />
                     <button className="bg-sky-600 hover:bg-sky-700 px-3 py-2 rounded-lg text-white transition-colors">
                        {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                     </button>
                  </div>
               </div>
            </div>

            <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-slate-500">
               <p>{t.footer.rights}</p>
               <div className="flex gap-6">
                  <a href="#" className="hover:text-white transition-colors">{isRTL ? 'سياسة الخصوصية' : 'Privacy Policy'}</a>
                  <a href="#" className="hover:text-white transition-colors">{isRTL ? 'شروط الاستخدام' : 'Terms of Service'}</a>
               </div>
            </div>
         </div>
      </footer>
    </div>
  );
};

export default LandingPage;








